import { MatchEvent } from "../../store/useMatchLogStore";
import type { ImageData, MatchTeamKit } from "../../types";
export type { MatchEvent };

export interface Player {
  id: string;
  full_name: string;
  short_name?: string;
  jersey_number: number;
  position: string;
  birth_date?: string | null;
  is_starter?: boolean;
  photo_url?: string | null;
  photo_image?: ImageData | null;
}

/**
 * Free text at the API boundary — a manager-sourced entry is always the
 * literal "HEAD_COACH" (the only non-free-text value; MatchStaffRole is
 * retired, "ASSISTANT_1"/"ASSISTANT_2" do not exist as role values); a
 * technical_staff entry carries its stored TechnicalStaffRole English
 * value verbatim (e.g. "Fitness Coach"). Kept as a named alias (rather
 * than inlining `string`) so call sites document intent.
 */
export type StaffRole = string;

export interface StaffMember {
  staff_id: string;
  name: string;
  role: StaffRole;
}

export interface Team {
  id: string;
  team_id?: string;
  name: string;
  short_name: string;
  score?: number;
  logo_url?: string | null;
  logo_image?: ImageData | null;
  players: Player[];
  technical_staff?: StaffMember[];
  primary_color?: string | null;
  secondary_color?: string | null;
  /** Per-match kit (EPIC-112); `kit.shirt_color` wins over `primary_color` for analytics colours. */
  kit?: MatchTeamKit | null;
}

/**
 * Single source of truth for "who is this card about" — a card event is
 * mutually exclusive on player_id/staff_id (contract: FROZEN CONTRACT,
 * client wave 2026-08-05). Import this everywhere a card is grouped or
 * counted per-subject instead of re-deriving the player_id ?? staff_id
 * fallback inline.
 */
export const getCardSubjectId = (event: MatchEvent): string | null =>
  event.player_id ?? event.staff_id ?? null;

export type MatchStatus =
  | "Scheduled"
  | "Live"
  | "Halftime"
  | "Completed"
  | "Live_First_Half"
  | "Live_Second_Half"
  | "Fulltime"
  | "Abandoned"
  | "Pending"
  | "Live_Extra_First"
  | "Extra_Halftime"
  | "Live_Extra_Second"
  | "Penalties";

export type ClockMode = "EFFECTIVE" | "INEFFECTIVE";

export type IneffectiveAction =
  | "Goal"
  | "GoalKick"
  | "FreeKick"
  | "ThrowIn"
  | "Corner"
  | "VAR"
  | "Injury"
  | "OutOfBounds"
  | "Other";

export interface IneffectiveAggregateTotals {
  home: number;
  away: number;
  neutral: number;
}

export interface IneffectiveAggregatePeriod {
  totals: IneffectiveAggregateTotals;
  by_action: Record<IneffectiveAction, IneffectiveAggregateTotals>;
}

export interface IneffectiveAggregates {
  totals: IneffectiveAggregateTotals;
  by_action: Record<IneffectiveAction, IneffectiveAggregateTotals>;
  by_period: Record<string, IneffectiveAggregatePeriod>;
  active?: {
    team_key: "home" | "away" | "neutral";
    action: IneffectiveAction;
    period: number;
    start_timestamp: string;
  } | null;
  var_active?: {
    start_timestamp: string;
    period: number;
  } | null;
  timeout?: {
    total_seconds: number;
    active: {
      start_timestamp: string;
      period: number;
    } | null;
  } | null;
}

export interface Match {
  id: string;
  home_team: Team;
  away_team: Team;
  status: MatchStatus;
  match_time_seconds?: number;
  current_period_start_timestamp?: string;
  clock_seconds_at_period_start?: number;
  period_timestamps?: Record<
    string,
    { start?: string; end?: string; global_start_seconds?: number }
  >;
  ineffective_time_seconds?: number;
  ineffective_aggregates?: IneffectiveAggregates | null;
  clock_mode?: ClockMode;
  last_mode_change_timestamp?: string;
  share_token?: string;
}

export type EventType =
  | "Pass"
  | "Shot"
  | "Header"
  | "Duel"
  | "FoulCommitted"
  | "Card"
  | "Substitution"
  | "GameStoppage"
  | "VARDecision"
  | "Interception"
  | "Clearance"
  | "Block"
  | "Recovery"
  | "Offside"
  | "SetPiece"
  | "GoalkeeperAction"
  | "Carry";

export type ActionStep =
  | "selectPlayer"
  | "selectZone"
  | "selectQuickAction"
  | "selectDestination"
  | "selectAction"
  | "selectOutcome"
  | "selectRecipient";

export interface FieldCoordinate {
  xPercent: number;
  yPercent: number;
  statsbomb: [number, number];
  isOutOfBounds: boolean;
  outOfBoundsEdge?: "left" | "right" | "top" | "bottom" | null;
  /**
   * PMA-COCK-174: true when the tap landed in the goal-mouth band at the
   * left or right end of the pitch (see `TacticalField.tsx`'s
   * `buildCoordinate` for the exact thresholds, documented in
   * cockpit-contract.md). Optional — deliberately NOT set by
   * `useCockpitInteractionHandlers.ts`'s `handleFieldPlayerSelection`
   * (selectDestination branch), which builds its own `FieldCoordinate`-
   * shaped object by hand for an on-token tap rather than calling
   * `buildCoordinate`. That is intentional, not an oversight: it keeps a
   * tap that lands ON a goalkeeper/outfield player resolving through the
   * existing `targetPlayer` GK/Blocked credit path (PMA-COCK-172)
   * untouched — `goalMouth` only ever applies to a pure-geometry tap (open
   * pitch / out of bounds) via `handleFieldDestination`.
   */
  goalMouth?: "left" | "right" | null;
  /**
   * PMA-COCK-181 (dated reversal, EPIC-96, 2026-09-24): true when this
   * destination was built from a tap on the off-pitch on-target hint-bar
   * button (`ActionStage.tsx`'s `field-ontarget-btn`, via
   * `TacticalField.tsx`'s exported `buildOnTargetChipCoordinate`) rather
   * than a raw pitch tap. The interactive on-pitch `tactical-ontarget-chip`
   * this flag used to key off (a turquoise bolita rendered beside the
   * goalkeeper during a shot's `selectDestination` step) was retired
   * outright — the pitch itself now shows only a non-interactive shot guide
   * and tap ripple, both `pointer-events-none`. Optional, deliberately
   * never set by `buildCoordinate`'s own pure-geometry path —
   * `useActionFlow.ts`'s `handleDestinationClick` short-circuits to
   * `OnTarget` with no keeper/blocker credit fields when this is true, for
   * `DirectShot`/`Shot`/`Header`, then resets the flow synchronously so the
   * next tap starts a fresh action.
   */
  onTargetChip?: true;
}

export interface FieldAnchor {
  xPercent: number;
  yPercent: number;
}

export interface ActionConfig {
  actions: string[];
  outcomes?: Record<string, string[]>;
  needsRecipient?: boolean;
  /** Which roster the recipient step should offer. Only meaningful when
   * `needsRecipient` is true. Defaults to the acting player's own team when
   * omitted (matches every pre-existing `needsRecipient` config, e.g. Pass). */
  recipientTeam?: "own" | "opponent";
  /** When true, the recipient step offers a skip control (PMA-COCK-166) that
   * still dispatches the underlying event, just without the recipient's
   * data fields — distinct from Cancel, which discards the event entirely. */
  recipientOptional?: boolean;
  isSpecial?: boolean; // For actions requiring special UI (e.g., substitution flow)
}

export interface LoggerHarness {
  resetFlow: () => void;
  setSelectedTeam: (team: "home" | "away") => void;
  getCurrentStep: () => ActionStep;
  sendPassEvent: (options: {
    team: "home" | "away";
    passerId: string;
    recipientId: string;
  }) => void;
  sendRawEvent: (payload: Record<string, any>) => void;
  getMatchContext: () => {
    matchId: string;
    homeTeamId: string;
    awayTeamId: string;
  };
  undoLastEvent: () => Promise<void> | void;
  getQueueSnapshot: () => QueueSnapshot;
  getLiveEventSummary: () => LiveEventSummary;
  getLiveEvents: () => LiveEventDetail[];
  getRecentGameStoppages: () => GameStoppageSummary[];
  clearQueue: () => void;
  getDriftSnapshot: () => {
    computed: number;
    forced: number | null;
    effective: number;
    show: boolean;
  };
  /** PMA-COCK-115: content-level equivalent of the removed `ball-state-label`
   * DOM node — clock start/stop still drives `isBallInPlay`, only the visible
   * "Balón Fuera"/"Balón en Juego" indicator was removed. */
  getBallState: () => { isBallInPlay: boolean };
  /** PMA-COCK-118: content-level equivalent of the removed
   * `effective-clock-value`/`ineffective-clock-value` DOM nodes (the
   * "Tiempo Efectivo"/"Tiempo Inefectivo" cards) — the values themselves
   * (`useMatchTimer`'s `effectiveClock`/`ineffectiveClock`) are unchanged,
   * only their display in MatchTimerDisplay.tsx was removed. */
  getEffectiveIneffectiveClocks: () => {
    effectiveClock: string;
    ineffectiveClock: string;
  };
}

export interface QueuedEventSummary {
  match_id: string;
  timestamp: string;
  client_id?: string;
  type: string;
}

export interface QueueSnapshot {
  currentMatchId: string | null;
  queuedEvents: QueuedEventSummary[];
  queuedEventsByMatch: Record<string, QueuedEventSummary[]>;
}

export interface LiveEventSummary {
  liveCount: number;
  gameStoppageCount: number;
}

/**
 * PMA-COCK-111: content-level equivalent of LiveEventSummary, added so E2E
 * specs that used to assert on `LiveEventFeed`'s DOM (now removed from the
 * cockpit's render path) can verify event type/content against the same
 * underlying `liveEvents` store state instead — no UI navigation needed.
 */
export interface LiveEventDetail {
  type: string;
  team_id: string;
  player_id: string | null;
  staff_id: string | null;
  notes: string | null;
  match_clock: string;
  period: number;
  has_id: boolean;
  /** EPIC-97 (AC1): the event's own top-level origin `location`
   * (StatsBomb x/y), sibling to `data` — NOT inside it. Previously absent
   * from this harness-exposed shape entirely, which made the flip-rotation
   * invariant ("a tap on a flipped token stores the same location as
   * tapping it unflipped") unreachable from a real-browser E2E; only the
   * destination tap's `data.end_location` was readable. */
  location: [number, number] | null;
  /** Raw `event.data` (outcome, pass_type, receiver_id, etc.) — same object
   * the store already holds, exposed as-is for E2E content assertions. */
  data: Record<string, any>;
  /** Task #67: the event's own client_id, or null for the synthetic
   * system events (e.g. EffectiveTimeStart) that never carry one. */
  client_id: string | null;
}

export interface GameStoppageSummary {
  team_id: string;
  match_clock: string;
  notes?: string | null;
  stoppage_type: string;
  reason?: string | null;
  trigger_action?: string | null;
  trigger_team_id?: string | null;
}

export interface DuplicateMetadata {
  match_clock: string;
  period: number;
  team_id: string;
  existing_event_id?: string;
}

export interface DuplicateStats {
  count: number;
  lastTeamId?: string;
  lastEventType?: string;
  lastMatchClock?: string;
  lastSeenAt?: string;
}

export type LiveEvent = MatchEvent;

export interface TeamAdvancedStats {
  xg: number;
  xa: number;
  shots: number;
  key_passes: number;
  assists: number;
  progressive_passes: number;
  progressive_carries: number;
  duels_won: number;
  duels_lost: number;
  aerial_duels_won: number;
  aerial_duels_lost: number;
  interceptions: number;
  blocks: number;
  clearances: number;
  recoveries: number;
  set_piece_shots: number;
  set_piece_goals: number;
}

export interface PlayerAdvancedStats {
  player_id: string;
  team_side: "home" | "away";
  xg: number;
  xa: number;
  shots: number;
  key_passes: number;
  assists: number;
  progressive_passes: number;
  progressive_carries: number;
  duels_won: number;
  duels_lost: number;
  aerial_duels_won: number;
  aerial_duels_lost: number;
  interceptions: number;
  blocks: number;
  clearances: number;
  recoveries: number;
  pass_network: object;
}

export interface AdvancedMetrics {
  match_id?: string;
  generated_at: string | null;
  team_totals: {
    home: TeamAdvancedStats;
    away: TeamAdvancedStats;
  };
  players: PlayerAdvancedStats[];
  ball_share_by_period: Record<string, Record<string, number>>;
}
